import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import {
  Building2,
  BriefcaseMedical,
  Eye,
  Hash,
  Mail,
  Phone,
  Search,
  Stethoscope,
  User,
  X,
} from 'lucide-react'
import { doctorService } from '../api/services/doctor.service'
import { userService } from '../api/services/user.service'
import { PermissionGate } from '../components/auth/PermissionGate'
import { AppShell } from '../components/layout/AppShell'
import { Button } from '../components/ui/button'
import { FieldLabel } from '../components/ui/field-label'
import { IconActionButton, TableIconActions } from '../components/ui/table-icon-actions'
import { ListPagination } from '../components/ui/list-pagination'
import { Input } from '../components/ui/input'
import { SelectPicker, type SelectOption } from '../components/ui/select-picker'
import { DEFAULT_PAGE_SIZE, resolveListPage, type PaginationMeta } from '../lib/api-list'
import { useAuth } from '../context/AuthContext'
import { cn } from '../lib/utils'
import type { Doctor } from '../types/doctor'
import { REVIEWER_SALUTATION_OPTIONS } from '../types/reviewer'

type StatusFilter = 'all' | 'pending' | 'approved' | 'rejected'

const STATUS_OPTIONS: SelectOption[] = [
  { value: 'all', label: 'All statuses' },
  { value: 'pending', label: 'Pending' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
]

function formatSalutation(value: string) {
  return REVIEWER_SALUTATION_OPTIONS.find((option) => option.value === value)?.label ?? value
}

function DoctorStatusBadge({ doctor }: { doctor: Doctor }) {
  if (doctor.status === 'approved' && !doctor.is_active) {
    return <span className="badge bg-parch-100 text-ink-600">Disabled</span>
  }

  const styles = {
    pending: 'bg-warn-lt text-warn',
    approved: 'badge-success',
    rejected: 'bg-danger-lt text-danger',
  } as const

  return (
    <span className={cn('badge capitalize', styles[doctor.status])}>
      {doctor.status}
    </span>
  )
}

type DetailItemProps = {
  icon: ReactNode
  label: string
  children: ReactNode
  className?: string
}

function DetailItem({ icon, label, children, className }: DetailItemProps) {
  return (
    <div className={cn('rounded-md border border-parch-100 bg-parch-50/60 p-3', className)}>
      <div className="mb-1 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-ink-400">
        {icon}
        <span>{label}</span>
      </div>
      <div className="text-sm font-medium text-ink-900">{children}</div>
    </div>
  )
}

function DoctorDetailModal({
  doctor,
  acting,
  resending,
  can,
  onClose,
  onApprove,
  onReject,
  onDisable,
  onEnable,
  onResendCredentials,
}: {
  doctor: Doctor
  acting: boolean
  resending: boolean
  can: (permission: string) => boolean
  onClose: () => void
  onApprove: (doctor: Doctor) => void
  onReject: () => void
  onDisable: (doctor: Doctor) => void
  onEnable: (doctor: Doctor) => void
  onResendCredentials: (doctor: Doctor) => void
}) {
  const fullName = `${formatSalutation(doctor.salutation)} ${doctor.first_name} ${doctor.surname}`
  const initials = `${doctor.first_name.charAt(0)}${doctor.surname.charAt(0)}`.toUpperCase()
  const hospitals = doctor.hospitals ?? []

  return createPortal(
    <div className="fixed inset-0 z-[200] flex items-start justify-center overflow-y-auto bg-black/40 p-4 pt-20">
      <div className="my-4 flex w-full max-w-2xl flex-col overflow-hidden rounded-xl bg-white shadow-lg">
        <div className="shrink-0 border-b border-parch-100 bg-gradient-to-r from-green-50 to-white px-6 py-6">
          <div className="flex items-start justify-between gap-4">
            <div className="flex min-w-0 items-start gap-4">
              <div className="grid h-14 w-14 shrink-0 place-items-center rounded-full bg-green-600 text-lg font-bold text-white">
                {initials}
              </div>
              <div className="min-w-0">
                <h2 className="font-display text-xl font-bold text-ink-900">{fullName}</h2>
                <div className="mt-2 flex flex-wrap items-center gap-2 text-sm text-ink-600">
                  <span className="inline-flex items-center gap-1 rounded-md bg-white px-2 py-0.5 font-medium text-ink-800 ring-1 ring-parch-100">
                    <User className="h-3.5 w-3.5 text-ink-400" />
                  </span>
                  <span className="inline-flex min-w-0 items-center gap-1 truncate">
                    <Mail className="h-3.5 w-3.5 shrink-0 text-ink-400" />
                    {doctor.email}
                  </span>
                </div>
              </div>
            </div>
            <DoctorStatusBadge doctor={doctor} />
          </div>
        </div>

        <div className="min-h-0 max-h-[min(60vh,32rem)] flex-1 overflow-y-auto px-6 py-5">
          <p className="section-label mb-3">Professional details</p>
          <div className="grid gap-3 sm:grid-cols-2">
            <DetailItem icon={<Stethoscope className="h-3.5 w-3.5" />} label="Department">
              {doctor.department_name ?? '—'}
            </DetailItem>
            <DetailItem icon={<BriefcaseMedical className="h-3.5 w-3.5" />} label="Position">
              {doctor.position_name ?? '—'}
            </DetailItem>
            <DetailItem
              icon={<Building2 className="h-3.5 w-3.5" />}
              label="Hospitals"
              className="sm:col-span-2"
            >
              {hospitals.length > 0 ? (
                <div className="mt-0.5 flex flex-wrap gap-1.5">
                  {hospitals.map((hospital) => (
                    <span
                      key={hospital.id}
                      className="inline-flex rounded-full bg-white px-2.5 py-0.5 text-xs font-medium text-green-800 ring-1 ring-green-200"
                    >
                      {hospital.name}
                    </span>
                  ))}
                </div>
              ) : (
                '—'
              )}
            </DetailItem>
          </div>

          <p className="section-label mb-3 mt-6">Contact & credentials</p>
          <div className="grid gap-3 sm:grid-cols-2">
            <DetailItem icon={<Hash className="h-3.5 w-3.5" />} label="Registration number">
              {doctor.registration_number ?? '—'}
            </DetailItem>
            <DetailItem icon={<Phone className="h-3.5 w-3.5" />} label="Phone">
              {doctor.phone_number ?? '—'}
            </DetailItem>
          </div>

          <p className="section-label mb-3 mt-6">Doctor references</p>
          <div className="grid gap-3 sm:grid-cols-2">
            <DetailItem icon={<Stethoscope className="h-3.5 w-3.5" />} label="Proposer doctor">
              {[doctor.proposer_name, doctor.proposer_email, doctor.proposer_phone_number].filter(Boolean).join(' | ') || '—'}
            </DetailItem>
            <DetailItem icon={<Stethoscope className="h-3.5 w-3.5" />} label="Seconder doctor">
              {[doctor.seconder_name, doctor.seconder_email, doctor.seconder_phone_number].filter(Boolean).join(' | ') || '—'}
            </DetailItem>
          </div>

          {doctor.rejection_reason && (
            <div className="mt-5 rounded-md border border-danger/20 bg-danger-lt px-4 py-3">
              <p className="text-xs font-semibold uppercase tracking-wide text-danger">Rejection reason</p>
              <p className="mt-1 text-sm text-ink-800">{doctor.rejection_reason}</p>
            </div>
          )}
        </div>

        <div className="flex shrink-0 flex-wrap justify-end gap-2 border-t border-parch-100 bg-parch-50 px-6 py-4">
          <Button type="button" variant="secondary" onClick={onClose}>
            Close
          </Button>
          {can('users-resend-credentials') && (
            <Button
              type="button"
              variant="secondary"
              disabled={acting || resending}
              onClick={() => onResendCredentials(doctor)}
            >
              {resending ? 'Sending…' : 'Resend Creds Email'}
            </Button>
          )}
          {doctor.status === 'pending' && can('doctors-reject') && (
            <Button type="button" variant="destructive" disabled={acting} onClick={onReject}>
              Reject
            </Button>
          )}
          {(doctor.status === 'pending' || doctor.status === 'rejected') && can('doctors-approve') && (
            <PermissionGate permission="doctors-approve">
              <Button type="button" disabled={acting} onClick={() => onApprove(doctor)}>
                {acting ? 'Approving…' : 'Approve'}
              </Button>
            </PermissionGate>
          )}
          {doctor.status === 'approved' && doctor.is_active && can('doctors-disable') && (
            <Button
              type="button"
              variant="destructive"
              disabled={acting}
              onClick={() => onDisable(doctor)}
            >
              Disable
            </Button>
          )}
          {doctor.status === 'approved' && !doctor.is_active && can('doctors-disable') && (
            <Button type="button" disabled={acting} onClick={() => onEnable(doctor)}>
              {acting ? 'Enabling…' : 'Enable'}
            </Button>
          )}
        </div>
      </div>
    </div>,
    document.body,
  )
}

export function DoctorApprovalsPage() {
  const { can } = useAuth()
  const [status, setStatus] = useState<StatusFilter>('all')
  const [searchInput, setSearchInput] = useState('')
  const [search, setSearch] = useState('')
  const [doctors, setDoctors] = useState<Doctor[]>([])
  const [hasLoaded, setHasLoaded] = useState(false)
  const [viewing, setViewing] = useState<Doctor | null>(null)
  const [rejectOpen, setRejectOpen] = useState(false)
  const [rejectReason, setRejectReason] = useState('')
  const [acting, setActing] = useState(false)
  const [resendingId, setResendingId] = useState<string | null>(null)
  const [page, setPage] = useState(1)
  const [perPage, setPerPage] = useState<number>(DEFAULT_PAGE_SIZE)
  const [meta, setMeta] = useState<PaginationMeta | null>(null)

  const activeFilterCount = useMemo(() => {
    let count = 0
    if (search.trim()) count += 1
    if (status !== 'all') count += 1
    return count
  }, [search, status])

  const clearFilters = () => {
    setSearch('')
    setSearchInput('')
    setStatus('all')
  }

  const loadDoctors = useCallback(async () => {
    try {
      const response = await doctorService.list({
        page,
        per_page: perPage,
        status: status === 'all' ? undefined : status,
        search: search.trim() || undefined,
      })
      const { items, meta: nextMeta } = resolveListPage(response, page, perPage)
      setDoctors(items)
      setMeta(nextMeta)
    } catch {
      setDoctors([])
      setMeta(null)
    } finally {
      setHasLoaded(true)
    }
  }, [page, perPage, search, status])

  useEffect(() => {
    void loadDoctors()
  }, [loadDoctors])

  useEffect(() => {
    setPage(1)
  }, [search, status])

  const openView = async (doctor: Doctor) => {
    try {
      const response = await doctorService.show(doctor.id)
      setViewing(response.data)
    } catch {
      setViewing(doctor)
    }
  }

  const handleApprove = async (doctor: Doctor) => {
    setActing(true)
    try {
      await doctorService.approve(doctor.id)
      setViewing(null)
      await loadDoctors()
    } catch {
      // handled globally
    } finally {
      setActing(false)
    }
  }

  const handleReject = async () => {
    if (!viewing) {
      return
    }

    setActing(true)
    try {
      await doctorService.reject(viewing.id, rejectReason || undefined)
      setRejectOpen(false)
      setRejectReason('')
      setViewing(null)
      await loadDoctors()
    } catch {
      // handled globally
    } finally {
      setActing(false)
    }
  }

  const handleDisable = async (doctor: Doctor) => {
    setActing(true)
    try {
      await doctorService.disable(doctor.id)
      setViewing(null)
      await loadDoctors()
    } catch {
      // handled globally
    } finally {
      setActing(false)
    }
  }

  const handleEnable = async (doctor: Doctor) => {
    setActing(true)
    try {
      await doctorService.enable(doctor.id)
      setViewing(null)
      await loadDoctors()
    } catch {
      // handled globally
    } finally {
      setActing(false)
    }
  }

  const handleResendCredentials = async (doctor: Doctor) => {
    setResendingId(doctor.id)
    try {
      await userService.resendCredentials(doctor.id)
    } catch {
      // handled globally
    } finally {
      setResendingId(null)
    }
  }

  return (
    <AppShell>
      <div className="mx-auto max-w-6xl px-2 py-2">
        <div className="mb-6">
          <h1 className="font-display text-2xl font-bold">Doctors</h1>
          <p className="mt-1 text-sm text-ink-600">
            View all doctors and their status. Approve notifies by email without changing the password.
            Use Resend Creds Email to generate a new password and email it to the doctor.
          </p>
        </div>

        <section className="mb-6 rounded-xl border border-parch-100 bg-white p-4 shadow-card sm:p-5">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <h2 className="text-sm font-semibold text-ink-900">Filters</h2>
              {activeFilterCount > 0 && (
                <span className="inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-green-100 px-1.5 text-xs font-semibold text-green-700">
                  {activeFilterCount}
                </span>
              )}
            </div>
          </div>

          <div className="mt-4 flex flex-wrap items-end gap-2">
            <div className="min-w-[12rem] flex-1 basis-0">
              <FieldLabel>Name</FieldLabel>
              <div className="relative">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-400" />
                <Input
                  className="pl-9"
                  placeholder="Search name, email…"
                  value={searchInput}
                  onChange={(event) => setSearchInput(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      setSearch(searchInput.trim())
                    }
                  }}
                />
              </div>
            </div>
            <div className="min-w-[12rem] flex-1 basis-0">
              <FieldLabel>Status</FieldLabel>
              <SelectPicker
                options={STATUS_OPTIONS}
                value={status}
                onChange={(value) => setStatus(value as StatusFilter)}
                placeholder="All statuses"
              />
            </div>
            <Button onClick={() => setSearch(searchInput.trim())}>Search</Button>
            {activeFilterCount > 0 && (
              <Button variant="secondary" onClick={clearFilters}>
                <X className="h-4 w-4" />
                Clear
              </Button>
            )}
          </div>
        </section>

        <section className="overflow-hidden rounded-lg border border-parch-100 bg-white shadow-card">
          <div className="overflow-x-auto">
            <table className="min-w-full text-left text-sm">
              <thead className="border-b border-parch-100 bg-parch-50 text-ink-600">
                <tr>
                  <th className="px-4 py-3 font-medium">Name</th>
                  <th className="px-4 py-3 font-medium">Email</th>
                  <th className="px-4 py-3 font-medium">Department</th>
                  <th className="px-4 py-3 font-medium">Status</th>
                  <th className="px-4 py-3 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {hasLoaded && doctors.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-4 py-6 text-sm text-ink-500">
                      No doctors found.
                    </td>
                  </tr>
                ) : (
                  doctors.map((doctor) => (
                    <tr key={doctor.id} className="border-b border-parch-50 last:border-0">
                      <td className="px-4 py-3">
                        {doctor.first_name} {doctor.surname}
                      </td>
                      <td className="px-4 py-3">{doctor.email}</td>
                      <td className="px-4 py-3">{doctor.department_name ?? '—'}</td>
                      <td className="px-4 py-3">
                        <DoctorStatusBadge doctor={doctor} />
                      </td>
                      <td className="px-4 py-3">
                        <TableIconActions>
                          {can('doctors-show') && (
                            <IconActionButton
                              icon={Eye}
                              label="View"
                              onClick={() => void openView(doctor)}
                            />
                          )}
                          {can('users-resend-credentials') && (
                            <IconActionButton
                              icon={Mail}
                              label="Resend Creds Email"
                              disabled={resendingId === doctor.id}
                              onClick={() => void handleResendCredentials(doctor)}
                            />
                          )}
                        </TableIconActions>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </section>

        <ListPagination
          meta={meta}
          perPage={perPage}
          onPageChange={setPage}
          onPerPageChange={(n) => {
            setPerPage(n)
            setPage(1)
          }}
        />

        {viewing && (
          <DoctorDetailModal
            doctor={viewing}
            acting={acting}
            resending={resendingId === viewing.id}
            can={can}
            onClose={() => setViewing(null)}
            onApprove={(doctor) => void handleApprove(doctor)}
            onReject={() => setRejectOpen(true)}
            onDisable={(doctor) => void handleDisable(doctor)}
            onEnable={(doctor) => void handleEnable(doctor)}
            onResendCredentials={(doctor) => void handleResendCredentials(doctor)}
          />
        )}

        {rejectOpen && viewing &&
          createPortal(
            <div className="fixed inset-0 z-[210] flex items-center justify-center bg-black/40 p-4">
              <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-card">
                <h3 className="font-display text-lg font-bold">Reject application</h3>
                <p className="mt-1 text-sm text-ink-600">
                  Optionally provide a reason for rejecting {viewing.first_name} {viewing.surname}.
                </p>
                <div className="mt-4">
                  <FieldLabel optional>Reason</FieldLabel>
                  <Input value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} />
                </div>
                <div className="mt-6 flex justify-end gap-2">
                  <Button type="button" variant="secondary" onClick={() => setRejectOpen(false)}>
                    Cancel
                  </Button>
                  <Button type="button" variant="destructive" disabled={acting} onClick={() => void handleReject()}>
                    {acting ? 'Rejecting…' : 'Reject'}
                  </Button>
                </div>
              </div>
            </div>,
            document.body,
          )}
      </div>
    </AppShell>
  )
}
